import { SortingState } from '@tanstack/react-table';
import * as XLSX from 'xlsx';

export type TimeFormat = 'formatted' | 'minutes' | 'clock';
export type FormatTimeFn = (minutes: number, format: TimeFormat) => string;

export type RatioColumnLabels = {
  title: string;
  percentage: string;
  totalTime: string;
  count: string;
  averageTimePerCount: string;
  averageTimePerDay: string;
};

export type TransitionColumnLabels = {
  date: string;
  time: string;
};

// 日付をスラッシュ区切りからハイフン区切りに変換
const formatDateForExport = (value: any): any => {
  if (typeof value === 'string' && value.includes('/')) {
    return value.replace(/\//g, '-');
  }
  return value;
};

// 表のデータを取得する共通関数（表示中のデータのみ）
const getTableData = (
  table: any,
  timeFormat: TimeFormat,
  formatTime: FormatTimeFn,
): string[][] => {
  const headers = table
    .getFlatHeaders()
    .map((header: any) => header.column.columnDef.header);
  
  const rows = table.getRowModel().rows.map((row: any) =>
    row.getVisibleCells().map((cell: any) => {
      const value = cell.getContext().getValue();
      // 時間関連のカラムの場合はフォーマット
      if (typeof value === 'number' &&
        ['totalTime', 'averageTimePerCount', 'averageTimePerDay', 'time'].includes(cell.column.id)) {
        return formatTime(value, timeFormat);
      }
      // 日付カラムの場合はスラッシュをハイフンに変換
      if (cell.column.id === 'date') {
        return formatDateForExport(value);
      }
      return value;
    }),
  );

  return [headers, ...rows];
};

// 全件データを取得する関数
const getAllTableData = (
  data: any[],
  columns: any[],
  timeFormat: TimeFormat,
  formatTime: FormatTimeFn,
): string[][] => {
  const headers = columns.map((col: any) => col.header);
  
  const rows = data.map((row: any) =>
    columns.map((col: any) => {
      const accessorKey = col.accessorKey;
      const value = row[accessorKey];
      
      // 時間関連のカラムの場合はフォーマット
      if (typeof value === 'number' &&
        ['totalTime', 'averageTimePerCount', 'averageTimePerDay', 'time'].includes(accessorKey)) {
        return formatTime(value, timeFormat);
      }
      
      // 日付カラムの場合はスラッシュをハイフンに変換
      if (accessorKey === 'date') {
        return formatDateForExport(value);
      }
      
      return value;
    }),
  );

  return [headers, ...rows];
};

// 表をコピー用の関数
export const copyTableToClipboard = (
  table: any,
  timeFormat: TimeFormat,
  formatTime: FormatTimeFn,
  setCopied: (copied: boolean) => void,
): void => {
  const data = getTableData(table, timeFormat, formatTime);
  const csv = data.map((row) => row.join('\t')).join('\n');
  navigator.clipboard.writeText(csv).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  });
};

// 表をCSV形式でエクスポートする関数（全件データ）
export const exportTableToCSV = (
  data: any[],
  columns: any[],
  timeFormat: TimeFormat,
  formatTime: FormatTimeFn,
  filename: string = 'table.csv',
): void => {
  const exportData = getAllTableData(data, columns, timeFormat, formatTime);
  const ws = XLSX.utils.aoa_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, filename);
};

// 表をExcel形式でエクスポートする関数（全件データ）
export const exportTableToExcel = (
  data: any[],
  columns: any[],
  timeFormat: TimeFormat,
  formatTime: FormatTimeFn,
  filename: string = 'table.xlsx',
): void => {
  const exportData = getAllTableData(data, columns, timeFormat, formatTime);
  const ws = XLSX.utils.aoa_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, filename);
};

// 比率テーブルのカラム定義
export const getRatioColumns = (
  timeFormat: TimeFormat,
  formatTime: FormatTimeFn,
  labels: RatioColumnLabels,
) => [
  { accessorKey: 'title', header: labels.title },
  { accessorKey: 'percentage', header: labels.percentage },
  {
    accessorKey: 'totalTime',
    header: labels.totalTime,
    cell: (info: any) => formatTime(info.getValue() as number, timeFormat),
  },
  { accessorKey: 'count', header: labels.count },
  {
    accessorKey: 'averageTimePerCount',
    header: labels.averageTimePerCount,
    cell: (info: any) => formatTime(info.getValue() as number, timeFormat),
  },
  {
    accessorKey: 'averageTimePerDay',
    header: labels.averageTimePerDay,
    cell: (info: any) => formatTime(info.getValue() as number, timeFormat),
  },
];

// 遷移テーブルのカラム定義
export const getTransitionColumns = (
  timeFormat: TimeFormat,
  formatTime: FormatTimeFn,
  labels: TransitionColumnLabels,
) => [
  {
    accessorKey: 'date',
    header: labels.date,
    cell: (info: any) => formatDateForExport(info.getValue()),
  },
  {
    accessorKey: 'time',
    header: labels.time,
    cell: (info: any) => formatTime(info.getValue() as number, timeFormat),
  },
];

// デフォルトのソート状態
export const getDefaultSorting = (tableType: 'ratio' | 'transition'): SortingState => {
  return [{ id: tableType === 'ratio' ? 'percentage' : 'date', desc: false }];
};
